/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  BatchCallFormValues,
  BatchCallUpdateFormValues,
  RecipientUpdateFormValues,
} from '@/schemas/batch-call.schema';

import type {
  BatchCall,
  BatchCallDetail,
  BatchRecipient,
  CreateBatchCallPayload,
  UpdateBatchCallPayload,
  UpdateRecipientPayload,
} from '@/types/scheduler.type';

export const mapBatchCallCreatePayload = (values: BatchCallFormValues): CreateBatchCallPayload => {
  const scheduledAt = `${values.scheduledDate}T${values.scheduledTime}:00`;
  const timezone = values.scheduledTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    name: values.batchName,
    datetime: scheduledAt,
    timezone,
    agent_id: parseInt(values.agentId),
    recipients: values.recipients.map(r => ({
      name: r.name,
      phone_number: r.phoneNumber,
      metadata: r.metadata || {},
    })),
    ...(values.isRecurring && values.recurringFrequency && { every: values.recurringFrequency }),
    ...(values.isRecurring &&
      values.recurringFrequency === 'week' &&
      values.recurringDaysOfWeek?.length && { on: values.recurringDaysOfWeek as string[] }),
  };
};

export const mapBatchCallUpdatePayload = (
  values: BatchCallUpdateFormValues
): UpdateBatchCallPayload => {
  const scheduledAt = `${values.scheduledDate}T${values.scheduledTime}:00`;
  const timezone = values.scheduledTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    name: values.batchName,
    datetime: scheduledAt,
    timezone,
    agent_id: parseInt(values.agentId),
    ...(values.isRecurring && values.recurringFrequency && { every: values.recurringFrequency }),
    ...(values.isRecurring &&
      values.recurringFrequency === 'week' &&
      values.recurringDaysOfWeek?.length && { on: values.recurringDaysOfWeek as string[] }),
    ...(!values.isRecurring && { remove_recurrence: true }),
  };
};

export const mapScheduleResponse = (data: Record<string, any>): BatchCall => {
  return {
    id: data.id,
    batchName: data.name,
    totalRecipients: data.total_recipients,
    scheduledTime: data.scheduled_at,
    timezone: data.timezone,
    status: data.status,
    createdAt: data.created_at,
    agent: {
      id: data.agent.id,
      agentName: data.agent.agent_name,
      phoneNumber: data.agent.phone_number,
    },
    every: data.every ?? undefined,
    on: data.on ?? undefined,
  };
};

export const mapBatchCallDetailResponse = (response: Record<string, any>): BatchCallDetail => {
  const data = response.data;
  return {
    id: data.id,
    batchName: data.name,
    scheduledTime: data.scheduled_at,
    timezone: data.timezone,
    totalRecipients: data.total_recipients,
    pendingRecipients: data.pending_recipients,
    completedRecipients: data.completed_recipients,
    failedRecipients: data.failed_recipients,
    status: data.status,
    agent: {
      id: data.agent.id,
      agentName: data.agent.agent_name,
      phoneNumber: data.agent.phone_number,
    },
    createdAt: data.created_at,
  };
};

export const mapBatchRecipientResponse = (data: Record<string, any>): BatchRecipient => {
  return {
    id: data.id,
    name: data.name,
    phoneNumber: data.phone_number,
    status: data.status,
    calledAt: data.called_at ?? null,
    duration: data.duration ?? null,
    failureReason: data.job_metadata?.message ?? null,
    callId: data.call?.id ?? null,
  };
};

export const mapRecipientUpdatePayload = (
  values: RecipientUpdateFormValues
): UpdateRecipientPayload => {
  return {
    name: values.name,
    phone_number: values.phoneNumber,
  };
};
